import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

export type Language = "en" | "de";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    "nav.features": "Features",
    "nav.home": "Home",
    "nav.benefits": "Benefits",
    "nav.premium": "Premium",
    "nav.testimonials": "Reviews",
    "nav.download": "Waitlist",
    "nav.waitlist": "Waitlist",
    "nav.faq": "FAQ",
    "header.download": "Join Waitlist",
    "header.downloadNow": "Join Waitlist",
    "header.waitlist": "Join Waitlist",
    "header.waitlistJoin": "Join Waitlist",

    // Hero
    "hero.badge": "In development · Android planned",
    "hero.title1": "Keep your",
    "hero.title2": "groceries",
    "hero.title3": "in check",
    "hero.subtitle":
      "Save money. Reduce waste. FreshKeeper reminds you about expiring products on time. Simple, intuitive and efficient.",
    "hero.cta": "Join the Waitlist",
    "hero.scrollMore": "Discover more",
    "hero.imgAlt":
      "FreshKeeper App: Smart food management with expiry date tracking",

    // Features
    "features.badge": "Features",
    "features.title": "Everything you need",
    "features.subtitle":
      "FreshKeeper makes inventory management easy. Focus on what matters.",
    "features.inventory.title": "Current inventories",
    "features.inventory.desc":
      "Check your current inventories at a glance and always know what you have.",
    "features.expiry.title": "Expiring products",
    "features.expiry.desc":
      "Overview of expiring products so nothing goes to waste.",
    "features.push.title": "Push notifications",
    "features.push.desc":
      "Get push notifications before food expires. You decide when.",
    "features.filterSearch.title": "Filter & Search",
    "features.filterSearch.desc":
      "Filter by category and location or find products quickly with the powerful search.",
    "features.household.title": "Manage your household",
    "features.household.desc":
      "Manage your entire household inventory in one place.",
    "features.stats.title": "View statistics",
    "features.stats.desc":
      "View your own statistics and track your consumption.",

    // Why Section
    "why.badge": "Your benefits",
    "why.title": "Why FreshKeeper?",
    "why.subtitle":
      "In 2022, German households generated about 6.3 million tonnes of food waste, or 74.5 kg per person. This includes inedible parts and is not the amount of avoidable edible food.",
    "why.waste.title": "Reduces waste",
    "why.waste.desc":
      "Throw away less, enjoy more. Every saved food item counts for our environment.",
    "why.money.title": "Saves real money",
    "why.money.desc":
      "Keep track of food you already have before shopping again.",
    "why.time.title": "Saves valuable time",
    "why.time.desc":
      "No more wondering what is left. Shop faster, plan better.",
    "why.easy.title": "Simple & intuitive",
    "why.easy.desc": "No learning curve. Ready to go in 30 seconds.",
    "why.sustainable.title": "Sustainable everyday",
    "why.sustainable.desc":
      "Small habit, big impact. For a better world at your home.",
    "why.imgAlt":
      "FreshKeeper App Screenshot: Overview of your food with expiry dates",

    // Premium Section
    "premium.standardBadge": "Standard Version",
    "premium.standardTitle": "Full functionality, always free",
    "premium.standardDesc":
      "The standard version of FreshKeeper is fully functional and guaranteed ad-free. All essential features for managing your food inventory.",
    "premium.badge": "FreshKeeper Premium",
    "premium.title": "Unlock the full potential",
    "premium.subtitle":
      "Take your food management to the next level with exclusive premium features.",
    "premium.unlimited.title": "Unlimited items",
    "premium.unlimited.desc":
      "Add unlimited products to your inventory. No restrictions, no limits. Store everything you need.",
    "premium.households.title": "Multiple households",
    "premium.households.desc":
      "Manage shared flats or second homes separately. Switch between households with ease and stay organized.",
    "premium.custom.title": "Custom categories",
    "premium.custom.desc":
      "Create your own categories and storage locations. Customize the app to match your personal system.",
    "premium.stats.title": "Long-term statistics",
    "premium.stats.desc":
      "Analyze your consumption behavior and track your progress. Gain insights into your food management.",
    "premium.aichat.title": "AI chat",
    "premium.aichat.desc":
      "Generate recipes from your inventory and get tips on storage, meal prep and shopping in one assistant.",
    "premium.widgets.title": "Home screen widgets",
    "premium.widgets.desc":
      "Add widgets for soon-to-expire and already expired products directly to your phone home screen.",
    "premium.cta": "Get Premium",
    "premium.ctaNote": "Premium details will be announced before launch",

    // Download Section
    "download.title": "Get launch updates",
    "download.subtitle": "Neither the Android nor the iOS version has been released yet.",
    "download.cta": "Join the waitlist",

    // Waitlist Section
    "waitlist.title": "Be first to try FreshKeeper",
    "waitlist.subtitle":
      "Join our waitlist and we'll let you know as soon as the app is ready for you.",

    // FAQ
    "faq.title": "Frequently asked questions",
    "faq.subtitle": "Everything you need to know about FreshKeeper.",
    "faq.q1": "Has FreshKeeper launched yet?",
    "faq.a1":
      "No. Neither the Android nor the iOS version has been released yet. Join the waitlist to hear about launch availability.",
    "faq.q2": "Who is FreshKeeper for?",
    "faq.a2":
      "FreshKeeper is perfect for singles, couples, families and shared flats. Anyone who has food at home and wants to waste less. Whether fridge, freezer or pantry.",
    "faq.q3": "How do I create an account?",
    "faq.a3":
      "You can register easily with your email address or even faster with your Google account. Registration only takes a few seconds.",
    "faq.q4": "How do reminders work?",
    "faq.a4":
      "You set the expiry date for each product individually. In the notification settings, you choose when you want to be reminded before expiry, for example 3 days. FreshKeeper then automatically sends you push notifications for all products at your chosen time.",
    "faq.q5": "What does Premium include?",
    "faq.a5":
      "Premium unlocks unlimited items, multiple households, custom categories, long-term statistics and AI features like recipe generation and the AI assistant.",
    "faq.q6": "Which platforms are planned?",
    "faq.a6":
      "An Android version is planned, and an iOS version is also planned. Neither has been released yet.",

    // Footer
    "footer.desc":
      "A food management app in development. Android and iOS have not been released yet.",
    "footer.app": "App",
    "footer.legal": "Legal",
    "footer.imprint": "Imprint",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.withdrawal": "Right of Withdrawal",
    "footer.contact": "Contact",
    "footer.rights": "All rights reserved.",
    "footer.made": "Made with 💚 for a sustainable future",

    // Legal pages
    back: "Back to homepage",
  },
  de: {
    // Header
    "nav.features": "Funktionen",
    "nav.home": "Startseite",
    "nav.benefits": "Vorteile",
    "nav.premium": "Premium",
    "nav.testimonials": "Bewertungen",
    "nav.download": "Warteliste",
    "nav.waitlist": "Warteliste",
    "nav.faq": "FAQ",
    "header.download": "Warteliste",
    "header.downloadNow": "Warteliste beitreten",
    "header.waitlist": "Warteliste",
    "header.waitlistJoin": "Warteliste beitreten",

    // Hero
    "hero.badge": "In Entwicklung · Android geplant",
    "hero.title1": "Behalte deine",
    "hero.title2": "Lebensmittel",
    "hero.title3": "im Blick",
    "hero.subtitle":
      "Spare Geld. Vermeide Verschwendung. FreshKeeper erinnert dich rechtzeitig an ablaufende Produkte. Einfach, intuitiv und effizient.",
    "hero.cta": "Auf die Warteliste",
    "hero.scrollMore": "Mehr entdecken",
    "hero.imgAlt":
      "FreshKeeper App: Lebensmittel clever verwalten mit der smarten Vorratsverwaltung",

    // Features
    "features.badge": "Funktionen",
    "features.title": "Alles was du brauchst",
    "features.subtitle":
      "FreshKeeper macht Vorratsverwaltung einfach. Fokus auf das Wesentliche.",
    "features.inventory.title": "Aktuelle Bestände",
    "features.inventory.desc":
      "Sieh deine aktuellen Bestände auf einen Blick und wisse immer, was du hast.",
    "features.expiry.title": "Ablaufende Produkte",
    "features.expiry.desc":
      "Übersicht über ablaufende Produkte, damit nichts verschwendet wird.",
    "features.push.title": "Push-Benachrichtigungen",
    "features.push.desc":
      "Erhalte Push-Nachrichten bevor Lebensmittel ablaufen. Du entscheidest wann.",
    "features.filterSearch.title": "Filtern & Suchen",
    "features.filterSearch.desc":
      "Filtere nach Kategorie und Lagerort oder finde Produkte schnell mit der Suche.",
    "features.household.title": "Haushalt verwalten",
    "features.household.desc":
      "Verwalte deinen gesamten Haushaltsvorrat an einem Ort.",
    "features.stats.title": "Statistiken anzeigen",
    "features.stats.desc":
      "Sieh deine eigenen Statistiken und verfolge deinen Verbrauch.",

    // Why Section
    "why.badge": "Deine Vorteile",
    "why.title": "Warum FreshKeeper?",
    "why.subtitle":
      "2022 fielen in deutschen Privathaushalten rund 6,3 Millionen Tonnen Lebensmittelabfälle an, etwa 74,5 kg pro Kopf. Darin sind auch nicht essbare Bestandteile enthalten.",
    "why.waste.title": "Reduziert Verschwendung",
    "why.waste.desc":
      "Weniger wegwerfen, mehr genießen. Jedes gerettete Lebensmittel zählt für unsere Umwelt.",
    "why.money.title": "Spart bares Geld",
    "why.money.desc":
      "Behalte vorhandene Lebensmittel im Blick, bevor du erneut einkaufst.",
    "why.time.title": "Spart wertvolle Zeit",
    "why.time.desc":
      "Kein Grübeln mehr, was noch da ist. Schneller einkaufen, besser planen.",
    "why.easy.title": "Einfach & intuitiv",
    "why.easy.desc": "Keine Einarbeitung nötig. In 30 Sekunden startklar.",
    "why.sustainable.title": "Nachhaltiger Alltag",
    "why.sustainable.desc":
      "Kleine Gewohnheit, große Wirkung. Für eine bessere Welt bei dir zuhause.",
    "why.imgAlt":
      "FreshKeeper App Screenshot: Übersicht deiner Lebensmittel mit Haltbarkeitsdaten",

    // Premium Section
    "premium.standardBadge": "Standard Version",
    "premium.standardTitle": "Volle Funktionalität, immer kostenlos",
    "premium.standardDesc":
      "Die Standard-Version von FreshKeeper ist voll funktionsfähig und garantiert werbefrei. Alle wesentlichen Funktionen für deine Lebensmittelverwaltung.",
    "premium.badge": "FreshKeeper Premium",
    "premium.title": "Entfalte das volle Potenzial",
    "premium.subtitle":
      "Hebe deine Lebensmittelverwaltung auf das nächste Level mit exklusiven Premium-Funktionen.",
    "premium.unlimited.title": "Unbegrenzte Artikel",
    "premium.unlimited.desc":
      "Füge unbegrenzt viele Produkte zu deinem Vorrat hinzu. Keine Begrenzungen, keine Limits. Speichere alles, was du brauchst.",
    "premium.households.title": "Mehrere Haushalte",
    "premium.households.desc":
      "Verwalte WGs oder Zweitwohnungen getrennt. Wechsle mühelos zwischen Haushalten und behalte den Überblick.",
    "premium.custom.title": "Eigene Kategorien",
    "premium.custom.desc":
      "Erstelle eigene Kategorien und Lagerorte für perfekte Organisation. Passe die App an dein persönliches Lagersystem an.",
    "premium.stats.title": "Langzeit-Statistiken",
    "premium.stats.desc":
      "Analysiere dein Konsumverhalten und verfolge deinen Fortschritt. Gewinne Einblicke in deine Lebensmittelverwaltung.",
    "premium.aichat.title": "KI-Chat",
    "premium.aichat.desc":
      "Generiere Rezepte aus deinem Vorrat und erhalte Tipps zur Lagerung, zum Meal Prep und fürs Einkaufen.",
    "premium.widgets.title": "Home-Screen-Widgets",
    "premium.widgets.desc":
      "Füge Widgets für bald ablaufende und bereits abgelaufene Produkte direkt auf dem Home Screen deines Handys hinzu.",
    "premium.cta": "Premium holen",
    "premium.ctaNote": "Details zu Premium folgen vor dem Start",

    // Download Section
    "download.title": "Neuigkeiten zum Start erhalten",
    "download.subtitle":
      "Bisher ist weder die Android- noch die iOS-Version veröffentlicht.",
    "download.cta": "Warteliste beitreten",

    // Waitlist Section
    "waitlist.title": "Sei einer der Ersten",
    "waitlist.subtitle":
      "Trag dich in unsere Warteliste ein und wir benachrichtigen dich, sobald die App für dich bereit ist.",

    // FAQ
    "faq.title": "Häufig gestellte Fragen",
    "faq.subtitle": "Alles was du über FreshKeeper wissen musst.",
    "faq.q1": "Ist FreshKeeper schon veröffentlicht?",
    "faq.a1":
      "Nein. Weder die Android- noch die iOS-Version ist bisher veröffentlicht. Über die Warteliste kannst du dich zum Start informieren lassen.",
    "faq.q2": "Für wen ist FreshKeeper geeignet?",
    "faq.a2":
      "FreshKeeper ist perfekt für Singles, Paare, Familien und WGs. Also jeden, der Lebensmittel im Haushalt hat und weniger wegwerfen möchte. Egal ob Kühlschrank, Gefrierfach oder Vorratskammer.",
    "faq.q3": "Wie erstelle ich ein Konto?",
    "faq.a3":
      "Du kannst dich ganz einfach mit deiner E-Mail-Adresse registrieren oder noch schneller direkt mit deinem Google-Konto. Die Registrierung dauert nur wenige Sekunden.",
    "faq.q4": "Wie funktionieren die Erinnerungen?",
    "faq.a4":
      "Du legst für jedes Produkt individuell das Ablaufdatum fest. In den Benachrichtigungseinstellungen wählst du, wie viele Tage vor dem Ablauf du erinnert werden möchtest, zum Beispiel 3 Tage. FreshKeeper sendet dir dann automatisch Push-Benachrichtigungen für alle Produkte zu deinem gewählten Zeitpunkt.",
    "faq.q5": "Was beinhaltet Premium?",
    "faq.a5":
      "Premium schaltet unbegrenzte Artikel, mehrere Haushalte, eigene Kategorien, Langzeit-Statistiken und KI-Funktionen wie Rezeptgenerierung und den KI-Assistenten frei.",
    "faq.q6": "Für welche Plattformen ist FreshKeeper geplant?",
    "faq.a6":
      "Eine Android-Version ist geplant, außerdem ist eine iOS-Version geplant. Bisher wurde keine der beiden veröffentlicht.",

    // Footer
    "footer.desc":
      "Eine Lebensmittelverwaltungs-App in Entwicklung. Android und iOS sind bisher nicht veröffentlicht.",
    "footer.app": "App",
    "footer.legal": "Rechtliches",
    "footer.imprint": "Impressum",
    "footer.privacy": "Datenschutz",
    "footer.terms": "Nutzungsbedingungen",
    "footer.withdrawal": "Widerrufsbelehrung",
    "footer.contact": "Kontakt",
    "footer.rights": "Alle Rechte vorbehalten.",
    "footer.made": "Made with 💚 for a sustainable future",

    // Legal pages
    back: "Zurück zur Startseite",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const LanguageProvider = ({ children, initialLanguage }: { children: ReactNode; initialLanguage?: Language }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const pathname = typeof window !== "undefined" ? window.location.pathname : "";
    const englishLegalPath = /^\/(imprint|privacy-policy|terms-of-service|terms-and-conditions|right-of-withdrawal|delete-account|data-export)$/.test(pathname);
    const routeLanguage = initialLanguage ?? (pathname.startsWith("/en") || englishLegalPath
      ? "en"
      : pathname === "/"
        ? "de"
        : null);
    if (routeLanguage) return routeLanguage;
    const saved = typeof window !== "undefined" ? localStorage.getItem("freshkeeper-lang") : null;
    if (saved === "de" || saved === "en") return saved;
    // German is the primary language for freshkeeper.de.
    return "de";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("freshkeeper-lang", lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
